/**
 * Claim beacons in the local system (M5 spec §5, §14.4; plan C15).
 *
 * Every claimed system shows a Charter registry buoy (`prop.claim_beacon`, C6's greybox placeholder, in the class
 * and socket set of M3's `reach.beacon`) near the holder's first station, in the holder's livery:
 *
 * - **Anchor:** the holder's first drawn station (dockable, not a hidden template) in view order, else the
 *   system's first drawn station. No drawn station: no beacon.
 * - **Place:** `offsetM` (charter-conflict.json `beacons`) off the anchor's hull towards the star (the system origin):
 *   R + `offsetM` from its centre, R its gameplay radius (scale SC-5e2, the bounding sphere; `stationRadiusOf`), never
 *   past halfway; the buoy's −Z faces the star like the stations' bays.
 * - **Livery:** the holder's render look (M3's `factions.json`); a holder M3 does not list gets no beacon.
 * - **Flip:** a new holder re-places the beacon in its look (and at its first station); an unclaimed system
 *   removes it.
 *
 * `syncClaimBeacon` runs on every station sync (`strategic-link/stations.ts`) and talks to the session through
 * an optional prop hook (`placeProp` / `removeProp`). M3's session has no prop hook yet, and the model may be
 * missing from `assets.json`; either way the beacon is skipped with one log line (the gate or a render task adds
 * the hook). Rare events; allocates freely.
 */
import { z } from 'zod'
import assetsJson from '../data/assets.json'
import conflictJson from '../data/charter-conflict.json'
import templatesJson from '../data/strategic/templates.json'
import { logger } from '../core/logger'
import type { FactionsConfig } from '../galaxy/layout/config'
import { quatLookNegZ } from '../galaxy/layout/frame'
import type { QuatT, Vec3T } from '../galaxy/layout/types'
import type { FactionId as LookId } from '../render/contracts'
import { stationRadiusOf } from '../reach/session/siteRadius'
import { PLAYER_FACTION_ID, type CorpFactionId, type StationView } from '../strategic/types'

const beaconsConfigSchema = z
  .object({
    /** The beacon model (C6's placeholder). */
    assetId: z.string().min(1),
    /** Metres off the anchor station's hull (R + offsetM from its centre) towards the star. */
    offsetM: z.number().nonnegative(),
  })
  .strict()

/** Beacon tuning (charter-conflict.json `beacons`). */
export const BEACONS_CONFIG = beaconsConfigSchema.parse(conflictJson.beacons)

/** A prop the session places (and, with the same id, replaces). */
export interface PropPlacement {
  readonly id: string
  readonly assetId: string
  readonly look: LookId
  readonly position: Vec3T
  readonly rotation: QuatT
}

/** A system's claim beacon. */
export interface BeaconSpec extends PropPlacement {
  readonly systemId: string
  readonly holder: CorpFactionId
  /** The station it floats beside. */
  readonly anchorId: string
}

/** The optional session hooks that place and remove props (not on M3's session yet). */
export interface PropHost {
  placeProp(systemId: string, prop: PropPlacement): void
  removeProp(systemId: string, propId: string): void
}

/** Whether `host` has the prop hooks. */
export function isPropHost(host: object): host is PropHost {
  const h = host as Partial<PropHost>
  return typeof h.placeProp === 'function' && typeof h.removeProp === 'function'
}

/** Templates drawn as stations (not hidden bases). */
const HIDDEN_TEMPLATES: ReadonlySet<string> = new Set(templatesJson.templates.filter((t) => t.hidden).map((t) => t.id))
/** Model ids in the asset manifest. */
const MODEL_IDS: ReadonlySet<string> = new Set(assetsJson.models.map((m) => m.id))

/** Whether the asset manifest lists `assetId` (the default model check). */
export function manifestHasModel(assetId: string): boolean {
  return MODEL_IDS.has(assetId)
}

/** The beacon's prop id in `systemId`. */
export function beaconId(systemId: string): string {
  return `claim-beacon:${systemId}`
}

type AnchorView = Pick<StationView, 'id' | 'owner' | 'holder' | 'template' | 'dockable' | 'pos' | 'assetId'>

/** The anchor's gameplay radius R (metres; scale SC-5e2). */
export type AnchorRadius = (v: AnchorView) => number

/** The default `AnchorRadius`: R of the view's model (`stationRadiusOf(assetId)`). */
export function anchorRadius(v: AnchorView): number {
  return stationRadiusOf(v.assetId)
}

function drawn(v: AnchorView): boolean {
  return v.dockable && !HIDDEN_TEMPLATES.has(v.template)
}

/** The system's holder as its station views carry it (every station of a system has the same one), or null. */
export function holderOf(stations: readonly Pick<StationView, 'holder'>[]): CorpFactionId | null {
  for (const v of stations) if (v.holder !== null) return v.holder
  return null
}

/** The anchor station: the holder's first drawn station, else the system's first drawn station, else null. */
export function beaconAnchor<T extends AnchorView>(stations: readonly T[], holder: CorpFactionId): T | null {
  let first: T | null = null
  for (const v of stations) {
    if (!drawn(v)) continue
    if (v.owner === holder) return v
    first ??= v
  }
  return first
}

/**
 * `offsetM` off the hull of a station of radius `radiusM` at `pos` (R + `offsetM` from its centre) towards the origin,
 * at most halfway; +X from a station at the origin.
 */
export function beaconPosition(pos: readonly [number, number, number], offsetM: number, radiusM = 0): Vec3T {
  const d = Math.hypot(pos[0], pos[1], pos[2])
  const out = radiusM + offsetM
  if (!(d > 0)) return [out, 0, 0]
  const step = Math.min(out, d / 2) / d
  return [pos[0] - pos[0] * step, pos[1] - pos[1] * step, pos[2] - pos[2] * step]
}

/** The render look of a holder in M3's factions (the corporation's look for `'player'`), or null when it is not listed. */
function lookOf(holder: CorpFactionId, factions: FactionsConfig): LookId | null {
  if (holder === PLAYER_FACTION_ID) return factions.corporation?.look ?? null
  return Object.hasOwn(factions.factions, holder) ? factions.factions[holder as keyof FactionsConfig['factions']].look : null
}

/** The claim beacon of a system from its station views, or null when it is unclaimed or has nowhere to put one. */
export function claimBeacon(
  systemId: string,
  stations: readonly AnchorView[],
  factions: FactionsConfig,
  cfg: z.infer<typeof beaconsConfigSchema> = BEACONS_CONFIG,
  radiusOf: AnchorRadius = anchorRadius,
): BeaconSpec | null {
  const holder = holderOf(stations)
  if (holder === null) return null
  const look = lookOf(holder, factions)
  if (look === null) return null
  const anchor = beaconAnchor(stations, holder)
  if (anchor === null) return null
  const position = beaconPosition(anchor.pos, cfg.offsetM, radiusOf(anchor))
  const d = Math.hypot(position[0], position[1], position[2])
  const rotation = d > 0 ? quatLookNegZ([-position[0], -position[1], -position[2]]) : quatLookNegZ([0, 0, -1])
  return { id: beaconId(systemId), systemId, holder, anchorId: anchor.id, assetId: cfg.assetId, look, position, rotation }
}

export type BeaconOutcome = 'placed' | 'relivered' | 'moved' | 'removed' | 'unchanged' | 'skipped'

export interface BeaconDeps {
  /** Whether the model exists (default: the asset manifest lists it). */
  hasModel?(assetId: string): boolean
  readonly cfg?: z.infer<typeof beaconsConfigSchema>
  /** The anchor's radius (default `anchorRadius`, the view's model). */
  radiusOf?: AnchorRadius
}

/** Per host: each system's placed beacon. */
const placed = new WeakMap<object, Map<string, BeaconSpec>>()
/** Hosts already told (once) that they lack the prop hooks. */
const warnedHosts = new WeakSet<object>()
let warnedModel = false

function samePlace(a: BeaconSpec, b: BeaconSpec): boolean {
  return a.assetId === b.assetId && a.anchorId === b.anchorId && a.position.every((x, i) => x === b.position[i])
}

/**
 * Brings one system's claim beacon up to date on the host: places it, re-places it in the new holder's look on a
 * flip (or beside a new anchor), removes it when the system goes unclaimed. Skips (one log line) without the
 * model or the host's prop hooks.
 */
export function syncClaimBeacon(host: object, systemId: string, stations: readonly AnchorView[], factions: FactionsConfig, deps: BeaconDeps = {}): BeaconOutcome {
  const spec = claimBeacon(systemId, stations, factions, deps.cfg, deps.radiusOf)
  const bySystem = placed.get(host)
  const prev = bySystem?.get(systemId)
  if (spec === null) {
    if (prev === undefined || bySystem === undefined || !isPropHost(host)) return 'unchanged'
    bySystem.delete(systemId)
    try {
      host.removeProp(systemId, prev.id)
    } catch (err: unknown) {
      logger.error(`charter: removing the claim beacon of ${systemId} failed`, err)
    }
    return 'removed'
  }
  const hasModel = deps.hasModel ?? manifestHasModel
  if (!hasModel(spec.assetId)) {
    if (!warnedModel) {
      warnedModel = true
      logger.warn(`charter: claim beacons skipped: the model ${spec.assetId} is missing`)
    }
    return 'skipped'
  }
  if (!isPropHost(host)) {
    if (!warnedHosts.has(host)) {
      warnedHosts.add(host)
      logger.warn('charter: the Reach session has no placeProp; claim beacons are not drawn')
    }
    return 'skipped'
  }
  if (prev !== undefined && prev.look === spec.look && samePlace(prev, spec)) return 'unchanged'
  let map = bySystem
  if (map === undefined) {
    map = new Map()
    placed.set(host, map)
  }
  map.set(systemId, spec)
  try {
    host.placeProp(systemId, { id: spec.id, assetId: spec.assetId, look: spec.look, position: spec.position, rotation: spec.rotation })
  } catch (err: unknown) {
    logger.error(`charter: placing the claim beacon of ${systemId} failed`, err)
  }
  if (prev === undefined) return 'placed'
  return prev.look === spec.look ? 'moved' : 'relivered'
}
